import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { updateJob } from '../../../redux/slices/jobScheduleSlice';
import SaveBanner from '../../JobDetailPage/Mechanical/SaveBanner';
import useScheduleChangeLog from '../../../functions/useScheduleChangeLog';
import { patchData } from '../../../redux/slices/apiCallSlice';
// Import centralized date utilities
import { formatDateForForm } from '../../../utils/dateUtils';
import { getMoveRequestIcon } from '../utils/jobHelpers';

const MoveRequestModal = ({ show, job, onHide, onEditJob, canEdit = false }) => {
  const apiUrls = useSelector((state) => state.apiUrls.urls);
  const dispatch = useDispatch();
  const { logScheduleChange } = useScheduleChangeLog();
  
  // State for the new ship date
  const [newShipDate, setNewShipDate] = useState('');
  
  // State for the move up/out selection
  const [moveUpOut, setMoveUpOut] = useState(job?.MoveUpOut || '');
  
  // SaveBanner state
  const [showSaveBanner, setShowSaveBanner] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  
  // State for the reschedule button
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // State for the confirmation dialog
  const [showConfirmEdit, setShowConfirmEdit] = useState(false);
  // Use centralized date formatting function
  const formatDateForInput = (dateString) => formatDateForForm(dateString);

  const handleSubmit = async () => {
    // Don't allow submitting if the user doesn't have edit permission
    if (!canEdit) {
      setSaveStatus("You don't have permission to reschedule jobs");
      setShowSaveBanner(true);
      setTimeout(() => setShowSaveBanner(false), 3000);
      return;
    }
    
    if (!newShipDate) {
      setSaveStatus("Please select a new ship date");
      setShowSaveBanner(true);
      setTimeout(() => setShowSaveBanner(false), 3000);
      return;
    }

    // Disable the button
    setIsSubmitting(true);

    try {
      const resolvedJobId = job?.ID ?? job?.ProjectID ?? null;
      if (!resolvedJobId) {
        throw new Error('Missing job ID for reschedule request');
      }

      // Store the original date for logging
      const originalShipDate = job.CurrentPlanShipDate;
      
      // Prepare the patch data
      const patchPayload = {
        ID: resolvedJobId,
        CurrentPlanShipDate: newShipDate,
        MoveRequestDateMovedUpOut: newShipDate,
        MoveUpOut: moveUpOut || null
      };
      
      // Make the PATCH request
      await dispatch(
        patchData({
          url: `${apiUrls.generalProject}?id=${resolvedJobId}`,
          data: patchPayload,
        })
      ).unwrap();
      
      // Log changes to the schedule change log
      await logScheduleChange(
        resolvedJobId,
        'CurrentPlanShipDate',
        originalShipDate || '',
        newShipDate
      );
      
      // Update Redux store
      dispatch(updateJob({
        ID: resolvedJobId,
        CurrentPlanShipDate: newShipDate,
        MoveRequestDateMovedUpOut: newShipDate,
        MoveUpOut: moveUpOut || null
      }));
      
      // Show success notification
      setSaveStatus("Job rescheduled successfully");
      setShowSaveBanner(true);
      
      // Show the confirmation dialog instead of automatically closing
      setTimeout(() => {
        setShowSaveBanner(false);
        setShowConfirmEdit(true);
      }, 1500);
    } catch (error) {
      console.error('Error rescheduling job:', error);
      
      // Show error notification
      setSaveStatus(`Failed to reschedule job: ${error.message}`);
      setShowSaveBanner(true);
      
      // Re-enable the button on error
      setIsSubmitting(false);
      
      // Hide the notification after 5 seconds
      setTimeout(() => {
        setShowSaveBanner(false);
      }, 5000);
    }
  };

  // Handle edit job confirmation
  const handleConfirmEdit = (confirmed) => {
    setShowConfirmEdit(false);
    
    if (confirmed && canEdit) {
      // Close this modal first
      onHide();
      // Then trigger the edit job modal with a small delay
      // This prevents modal conflicts since we're transitioning from one modal to another
      setTimeout(() => {
        if (onEditJob) {
          onEditJob(job);
        }
      }, 100);
    } else {
      onHide();
    }
  };

  return (
    <>
      <Modal className="job-schedule-modal" show={show} onHide={onHide} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{canEdit ? 'Reschedule Job' : 'View Move Request'} {job?.JobNumber}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showSaveBanner && <SaveBanner showNotification={showSaveBanner} saveStatus={saveStatus} />}
          {!canEdit && (
            <div className="alert alert-info mb-3">
              You are in view-only mode. Only administrators and scheduling staff can reschedule jobs.
            </div>
          )}
          <Form>
            {job?.MoveRequestDateMovedUpOut && (
              <Row className="mb-3">
                <Col>
                  <div className="alert alert-info d-flex align-items-center">
                    {getMoveRequestIcon(job) && (
                      <i 
                        className={`${getMoveRequestIcon(job)} me-2`}
                        style={{ color: 'green', fontSize: '1.2em' }}
                      ></i>
                    )}
                    <span>
                      <strong>Current Status:</strong> This job has been moved {job.MoveUpOut ? job.MoveUpOut.toLowerCase() : ''} 
                      {job.MoveRequestDateMovedUpOut && ` on ${formatDateForInput(job.MoveRequestDateMovedUpOut)}`}
                    </span>
                  </div>
                </Col>
              </Row>
            )}
            <Row className="mb-3">
              <Col>
                <Form.Group controlId="currentShipDate">
                  <Form.Label>Current Planned Ship Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formatDateForInput(job?.CurrentPlanShipDate) || ''}
                    disabled
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group controlId="newShipDate">
                  <Form.Label>New Ship Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newShipDate}
                    onChange={(e) => setNewShipDate(e.target.value)}
                    required
                    disabled={isSubmitting || !canEdit}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group controlId="moveUpOut">
                  <Form.Label>
                    Move Type 
                    {getMoveRequestIcon({ ...job, MoveUpOut: moveUpOut, MoveRequestDateMovedUpOut: newShipDate }) && (
                      <i 
                        className={`${getMoveRequestIcon({ ...job, MoveUpOut: moveUpOut, MoveRequestDateMovedUpOut: newShipDate })} ms-2`}
                        style={{ color: 'blue' }}
                      ></i>
                    )}
                  </Form.Label>
                  <Form.Select
                    value={moveUpOut}
                    onChange={(e) => setMoveUpOut(e.target.value)}
                    disabled={isSubmitting || !canEdit}
                  >
                    <option value="">Select move type...</option>
                    <option value="Up">Move Up (earlier date)</option>
                    <option value="Out">Move Out (later date)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
            {canEdit ? 'Cancel' : 'Close'}
          </Button>
          {canEdit && (
            <Button 
              variant="primary" 
              onClick={handleSubmit} 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Reschedule'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Confirmation Dialog for Edit Job */}
      <Modal className="job-schedule-modal" show={showConfirmEdit} onHide={() => handleConfirmEdit(false)} backdrop="static">
        <Modal.Header>
          <Modal.Title>Job Rescheduled</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Job {job?.JobNumber} has been rescheduled successfully. Do you want to edit the job details?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => handleConfirmEdit(false)}>
            No
          </Button>
          <Button variant="primary" onClick={() => handleConfirmEdit(true)}>
            Yes, Edit Job
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

MoveRequestModal.propTypes = {
  show: PropTypes.bool.isRequired,
  job: PropTypes.object,
  onHide: PropTypes.func.isRequired,
  onEditJob: PropTypes.func,
  canEdit: PropTypes.bool
};

export default MoveRequestModal;
